import { FC, useEffect, useState } from "react";
import { clientConfig, SetS3Config } from "../../../awsClientConfig";
import { GetLegacyTaxRiskReports } from "../../../queries/GetLegacyTaxRiskReports";
import { Auth, Storage } from "aws-amplify";
import { userProfileData } from "../../../Common/profileInfo";
import { GetTaxRiskReports } from "../../../queries/GetTaxRiskReports";
import { IsUserLegacy } from "../../../Common/LegacyLogin";
import { TaxRiskReportItem } from "../../DashBoards/types";
import { dateformat } from "../../../functions/misc";
import {
  Box,
  Container,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import PersonIcon from "@mui/icons-material/Person";
import "./LegacyReports.css";
import PlaceholderReport from "../reports/PlaceholderReport";
import { LegacyTaxRiskReportItem } from '../../../API';

type Props = {
  clientName: string;
  legacyTaxReports: LegacyTaxRiskReportItem[];
};

type CombinedTaxReportItem = TaxRiskReportItem | LegacyTaxRiskReportItem;

const LegacyReports = ({ clientName, legacyTaxReports }: Props) => {
  const client = new clientConfig();
  const [isLegacy, setIsLegacy] = useState(false);
  const [taxReports, setTaxReports] = useState<CombinedTaxReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState();
  const [S3Bucket, setS3Bucket] = useState("");
  const [S3Key, setS3Key] = useState("");
  const [PDFReport, setPDFReport] = useState("");
  const [currentReport, setCurrentReport] = useState<any>();
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(false);
  const reportDate = "2024-05-15";

  useEffect(() => {
    fetchGetTaxReports();
  }, []);

  useEffect(() => {
    if (isLegacy && legacyTaxReports.length > 0) {
      fetchLegacyTaxRiskReports(legacyTaxReports);
    }
  }, [isLegacy]);

  const fetchLegacyTaxRiskReports = async (legacyTaxReports: LegacyTaxRiskReportItem[]) => {
    try {
      if (legacyTaxReports.length > 0) {
        setS3Bucket(legacyTaxReports[0].S3Bucket);
        setS3Key(legacyTaxReports[0].S3Key);
        await fetchPDFReport(legacyTaxReports[0].S3Bucket, legacyTaxReports[0].S3Key);
        const reportsArray: CombinedTaxReportItem[] = [
          ...taxReports,
          ...legacyTaxReports,
        ];
        setCurrentReport(reportsArray[0]);
        if (reportsArray.length > 12) {
          reportsArray.length = 12;
        }
        setTaxReports(reportsArray);
      } else {
        setCurrentReport(taxReports[0]);
      }
    } catch (error) {
      setShowPlaceholder(true);
      console.log("error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGetTaxReports = async () => {
    setLoading(true);
    await Auth.currentAuthenticatedUser({ bypassCache: true });

    const profileId = await userProfileData();
    setUserId(profileId);

    await client
      .query({
        query: GetTaxRiskReports,
        variables: { ProfileId: profileId },
      })
      .then(async (res: any) => {
        if (res.data.GetTaxRiskReports !== null) {
          setTaxReports(res.data.GetTaxRiskReports?.TaxRiskReports);
          const taxReports: any = res.data.GetTaxRiskReports?.TaxRiskReports;
          setTaxReports(taxReports);
        } else {
          setShowPlaceholder(true);
        }
      })
      .catch((error) => {
        setShowPlaceholder(true);
        console.log("error:", error);
      })
      .finally(() => {
        setLoading(true);
      });

    const legacyResponse = await IsUserLegacy();
    if (legacyResponse) {
      setIsLegacy(true);
    }
  };

  const printImage = () => {
    fetch(PDFReport)
      .then(async (response) => {
        return await response.arrayBuffer();
      })
      .then((result) => {
        const newBlob = new Blob([result], { type: "application/pdf" });
        const fileLink = window.URL.createObjectURL(newBlob);
        const iframe = document.createElement("iframe");
        iframe.src = fileLink;
        iframe.id = "print_pdf";
        iframe.name = "print_pdf";
        iframe.style.display = "none";
        document.body.appendChild(iframe);
        const windowVal: any = window;
        windowVal.frames["print_pdf"].focus();
        windowVal.frames["print_pdf"].print();
      })
      .catch((error) => console.log("error", error));
  };

  const onDownload = async () => {
    const date = " - " + dateformat(currentReport?.ReportDate) || "";
    try {
      SetS3Config(S3Bucket, "public");
      const Agreement = await Storage.get(S3Key, {
        customPrefix: { public: "" },
        download: true,
        level: "public",
        contentType: "application/pdf",
      });
      // @ts-ignore
      downloadBlob(Agreement.Body, `Monthly Summary${date}`);
    } catch (error) {
      console.log("error:", error);
    }
  };

  const downloadBlob = (blob: any, filename: any) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const fetchPDFReport = async (S3Bucket: string, S3Key: string) => {
    try {
      SetS3Config(S3Bucket, "public");
      const pdfReport = await Storage.get(S3Key, {
        customPrefix: { public: "" },
        level: "public",
        contentType: "application/pdf",
      });
      setPDFReport(pdfReport);
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <>
      {showPlaceholder ? (
        <PlaceholderReport clientName={clientName} />
      ) : (
        <Container>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "32px 24px 16px 24px",
              }}
            >
              <div style={{ padding: "0px 16px 0px 0px" }}>
                <PersonIcon
                  sx={{
                    borderRadius: "50%",
                    fontSize: "40px",
                    padding: "5px",
                    boxShadow: "0px 3px 14px 0px #00000014",
                  }}
                />
              </div>
              {loading && clientName ? (
                <Skeleton width={400} />
              ) : (
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "500", fontSize: "18px" }}
                >
                  {clientName}
                </Typography>
              )}
            </div>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "80%",
              }}
            >
              {loading ? (
                <Skeleton width={400} />
              ) : (
                <Select
                  sx={{
                    width: "40%",
                    boxShadow: "0px 1px 2px 0px #00000014",
                    border: "1px solid #1D89D2",
                    backgroundColor: "#F0F7FC",
                    padding: "20px 12px 20px 12px",
                    gap: 3,
                    textAlign: "center",
                    ".MuiSelect-select": {
                      display: "flex",
                      justifyContent: "center",
                    },
                  }}
                  value={reportDate}
                  onChange={() => {}}
                >
                  <MenuItem value={reportDate}>{dateformat(reportDate)}</MenuItem>
                </Select>
              )}
            </Box>
          </Box>
          {PDFReport ? (
            <>
              <div className="text-end" style={{ padding: "20px 5px" }}>
            <span className="letsdolater" onClick={printImage}>
              <PrintOutlinedIcon /> Print
            </span>
                <span
                  style={{ marginLeft: "30px" }}
                  className="letsdolater"
                  onClick={onDownload}
                >
              <ArrowDownwardOutlinedIcon /> Download
            </span>
              </div>
              <div style={{ marginTop: 10, height: 750 }}>
                <iframe
                  src={PDFReport}
                  height="100%"
                  width="100%"
                  id="muimh"
                  title="test"
                ></iframe>
              </div>
            </>
          ) : (
            !loading && (
              <div className="no-legacy-reports">No Legacy Reports Found</div>
            )
          )}
        </Container>
      )}
    </>
  );
};

export default LegacyReports;
